import React, { useState, useEffect, useContext, useCallback } from 'react';
import moment from 'moment-timezone';
import { useNavigate } from "react-router-dom";
import AuthContext from '../../contexts/auth-context';
import { getQueuesAPI } from '../../util';

export default function EventLocationController({ children, locationData, locationId }) {
  const navigate = useNavigate();
  const authCtx = useContext(AuthContext);
  const currentLocation = locationData;
  const [loading, setLoading] = useState(true);
  const [regularDayCount, setRegularDayCount] = useState(0);
  const [regularDayCountOut, setRegularDayCountOut] = useState(0);
  const [currentCount, setCurrentCount] = useState(0);
  const [currentTimestamp, setCurrentTimestamp] = useState(null);
  const [doorData, setDoorData] = useState([]);
  const [sensorData, setSensorData] = useState({});
  const [isDoorSensorActive, setIsDoorSensorActive] = useState(false);
  const [autoFetchEnabled, setAutoFetchEnabled] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [isToday, setIsToday] = useState(false);
  
  // Validate locationData has required calendar fields
  useEffect(() => {
    if (locationData) {
      if (!locationData.calendarMinDate || !locationData.calendarMaxDate || !locationData.calendarEnabledDates) {
        setApiError({
          message: 'Location configuration error: Missing required calendar dates',
          status: 'config_error'
        });
      }
    }
  }, [locationData]);

  // Initialize selectedDate based on location's timezone and calendar constraints
  useEffect(() => {
    if (!locationData || selectedDate !== null) return;

    const timezone = locationData.timeZone || 'America/New_York';
    
    // Get current date in the location's timezone (date only, no time)
    const currentDateInLocationTz = moment.tz(timezone).format('YYYY-MM-DD');
    
    // Parse calendar dates - the dates from locationData are already in YYYY-MM-DD format
    // and should be interpreted as-is in the location's timezone
    const minDate = locationData.calendarMinDate; // Already 'YYYY-MM-DD'
    const maxDate = locationData.calendarMaxDate; // Already 'YYYY-MM-DD'
    const enabledDates = locationData.calendarEnabledDates || []; // Already ['YYYY-MM-DD', ...]
    
    // Check if current date falls within min/max range and is enabled
    const isCurrentDateInRange = currentDateInLocationTz >= minDate && currentDateInLocationTz <= maxDate;
    const isCurrentDateEnabled = enabledDates.includes(currentDateInLocationTz);
    
    if (isCurrentDateInRange && isCurrentDateEnabled) {
      // Set to today (in location's timezone)
      // Parse the date string AS IF it's in the location's timezone
      const todayDate = moment.tz(currentDateInLocationTz, 'YYYY-MM-DD', timezone).toDate();
      setSelectedDate(todayDate);
      setIsToday(true);
      setAutoFetchEnabled(true);
    } else {
      // Fall back to minDate
      // Parse the date string AS IF it's in the location's timezone
      const minDateObj = moment.tz(locationData.calendarMinDate, 'YYYY-MM-DD', timezone).toDate();
      setSelectedDate(minDateObj);
      setIsToday(false);
      setAutoFetchEnabled(false);
    }
  }, [locationData, selectedDate]);

  // Recalculate isToday whenever selectedDate changes
  // useEffect(() => {
  //   if (!selectedDate || !locationData) return;

  //   const timezone = locationData.timeZone || 'America/New_York';
    
  //   // Get current date in location's timezone (date only)
  //   const currentDateInLocationTz = moment.tz(timezone).format('YYYY-MM-DD');
    
  //   // Get selected date in location's timezone (date only)
  //   const selectedDateInLocationTz = moment.tz(selectedDate, timezone).format('YYYY-MM-DD');
    
  //   // Compare dates
  //   const isTodayCheck = currentDateInLocationTz === selectedDateInLocationTz;
  //   setIsToday(isTodayCheck);
    
  //   // Enable auto-fetch only if viewing today
  //   setAutoFetchEnabled(isTodayCheck);
  // }, [selectedDate, locationData]);

  // Recalculate isToday whenever selectedDate changes
  useEffect(() => {
    if (!selectedDate || !locationData) return;

    const timezone = locationData.timeZone || 'America/New_York';
    
    // Get current date in location's timezone (date only, no time)
    const currentDateInLocationTz = moment.tz(timezone).format('YYYY-MM-DD');
    
    // Get the date component of selectedDate (year-month-day only, ignoring time/timezone)
    // This gives us the "calendar date" that was selected
    const selectedYear = selectedDate.getFullYear();
    const selectedMonth = String(selectedDate.getMonth() + 1).padStart(2, '0');
    const selectedDay = String(selectedDate.getDate()).padStart(2, '0');
    const selectedDateStr = `${selectedYear}-${selectedMonth}-${selectedDay}`;
    
    // Compare the calendar dates
    const isTodayCheck = currentDateInLocationTz === selectedDateStr;
    setIsToday(isTodayCheck);
    
    // Enable auto-fetch only if viewing today
    setAutoFetchEnabled(isTodayCheck);
  }, [selectedDate, locationData]);
  
  // Set minimum and maximum dates from locationData
  const minDate = locationData?.calendarMinDate;
  const maxDate = locationData?.calendarMaxDate;
  const enabledDates = locationData?.calendarEnabledDates;

  // Function to fetch event location data (uses midnight-11:59pm window)
  const fetchLocationData = useCallback(async (date = selectedDate) => {
    if (!date) return {
      regularDayCount: 0,
      regularDayCountOut: 0,
      currentCount: 0,
      currentTimestamp: null,
      doorData: [],
      sensorData: {},
      isDoorSensorActive: false
    };

    try {
      setApiError(null);
      
      const formattedDate = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth()+1).padStart(2,'0')}-${String(selectedDate.getDate()).padStart(2,'0')}`;

      return new Promise((resolve) => {
        getQueuesAPI("get-enterprise-event-daily-data",
          {
            timezone: currentLocation.timeZone,
            org: authCtx.org,
            locationId: currentLocation.id,
            measurement: currentLocation.measurement,
            doorMeasurement: currentLocation.doorMeasurement,
            doorCountSensorIds: currentLocation.doorCounterSensorIds,
            doorCounterSensorInfo: currentLocation.doorCounterSensorInfo,
            date: formattedDate
          },
          (data, error) => {
            if (error) {
              if (error.status === 401) {
                authCtx.logout();
                navigate("/auth");
              }
              console.error('Failed to fetch counter data:', error);
              setApiError(error);
              resolve({
                regularDayCount: 0,
                regularDayCountOut: 0,
                currentCount: 0,
                currentTimestamp: null,
                doorData: [],
                sensorData: {},
                isDoorSensorActive: false
              });
            } else if (data) {
              const totalData = data.totalData || [];
              const sensorData = data.sensorData || {};
              
              const latestTotal = totalData.length > 0 
                ? totalData[totalData.length - 1] 
                : { count: 0, countOut: 0, timestamp: null };
              
              resolve({
                currentCount: latestTotal.count || 0,
                currentTimestamp: latestTotal.timestamp || null,
                doorData: totalData, // This is the aggregated time-series data
                sensorData: sensorData, // Per-sensor breakdown
                isDoorSensorActive: true,
                regularDayCount: latestTotal.count || 0,
                regularDayCountOut: latestTotal.countOut || 0
              });
            } else {
              resolve({
                regularDayCount: 0,
                regularDayCountOut: 0,
                currentCount: 0,
                currentTimestamp: null,
                doorData: [],
                sensorData: {},
                isDoorSensorActive: false
              });
            }
          },
          authCtx.sessionToken
        );
      });
    } catch (error) {
      setApiError(error);
      return {
        regularDayCount: 0,
        regularDayCountOut: 0,
        currentCount: 0,
        currentTimestamp: null,
        doorData: [],
        sensorData: {},
        isDoorSensorActive: false
      };
    }
  }, [authCtx, navigate, selectedDate, locationData, locationId]);

  // Initial data fetch
  useEffect(() => {
    const loadInitialData = async () => {
      if (!authCtx.loggedIn) {
        navigate('/auth');
        return;
      }

      if (!selectedDate) {
        // Wait for selectedDate to be initialized
        return;
      }

      setLoading(true);
      const data = await fetchLocationData(selectedDate);
    //   console.log(data)
      setRegularDayCount(data.regularDayCount);
      setRegularDayCountOut(data.regularDayCountOut);
      setCurrentCount(data.currentCount);
      setCurrentTimestamp(data.currentTimestamp);
      setDoorData(data.doorData);
      setSensorData(data.sensorData);
      setIsDoorSensorActive(data.isDoorSensorActive);
      
      setLoading(false);
    };

    // Only fetch if we have the required dependencies
    if (authCtx && (locationData || locationId) && selectedDate) {
      loadInitialData();
    }
  }, [fetchLocationData, selectedDate, authCtx, locationData, locationId, navigate]);

  // Auto-refresh functionality (only for today)
  useEffect(() => {
    let interval;
    
    if (autoFetchEnabled && isToday && authCtx && (locationData || locationId) && selectedDate) {
      interval = setInterval(async () => {
        const data = await fetchLocationData(selectedDate);
        
        setRegularDayCount(data.regularDayCount);
        setRegularDayCountOut(data.regularDayCountOut);
        setCurrentCount(data.currentCount);
        setCurrentTimestamp(data.currentTimestamp);
        setDoorData(data.doorData);
        setSensorData(data.sensorData);
        setIsDoorSensorActive(data.isDoorSensorActive);
      }, 30000); // Refresh every 30 seconds
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [autoFetchEnabled, isToday, fetchLocationData, selectedDate, authCtx, locationData, locationId]);

  // Function to manually refresh data
  const refreshData = useCallback(async () => {
    if (!authCtx || (!locationData && !locationId) || !selectedDate) {
      console.warn('Missing required dependencies for refresh');
      return;
    }
    
    setLoading(true);
    const data = await fetchLocationData(selectedDate);
    
    setRegularDayCount(data.regularDayCount);
    setRegularDayCountOut(data.regularDayCountOut);
    setCurrentCount(data.currentCount);
    setCurrentTimestamp(data.currentTimestamp);
    setDoorData(data.doorData);
    setSensorData(data.sensorData);
    setIsDoorSensorActive(data.isDoorSensorActive);
    
    setLoading(false);
  }, [fetchLocationData, selectedDate, authCtx, locationData, locationId]);

  return children({
    loading,
    regularDayCount,
    regularDayCountOut,
    currentCount,
    currentTimestamp,
    doorData,
    sensorData,
    isDoorSensorActive,
    isToday,
    autoFetchEnabled,
    setAutoFetchEnabled,
    refreshData,
    authCtx,
    locationData,
    locationId,
    selectedDate,
    setSelectedDate,
    minDate,
    maxDate,
    enabledDates,
    showCalendar,
    setShowCalendar,
    apiError
  });
}